import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import orderBy from 'lodash/orderBy';
import sumBy from 'lodash/sumBy';

import type {
  ClusterOverviewOomKillsPoint,
  ClusterOverviewResponse,
  OrganizationOverviewResourceTimeseriesPoint,
  OrganizationOverviewSloMetricValue,
} from '@cast/types';
import {
  enhanceClusterSummary,
  type CostReportClusterSummaryNormalized,
} from '@cast/shared-cost-data-access';
import { toBig, toNumOrNull, toNumOrZero } from '@cast/utils';

import { mapOverviewSloToColumnData } from './mapOverviewSlo';
import type { SloColumnData } from './sloColumnData';
import type { OomKillDataPoint } from './oomKillsChart';
import type { ResourceChartDatapoints } from './mapResourceUsageChart';
import { deriveUsedFromProvisionedAndUtilization } from './overviewResourceMetrics';
import { EMPTY_METRIC_VALUE, type SloMetric } from './sloHealthMetrics';

dayjs.extend(utc);

/** Dashboard service-health tile order (wired metrics only). */
const SERVICE_HEALTH_METRIC_ORDER = [
  'nodeAvailability',
  'latency',
  'errRate',
  'oom',
] as const;

export const getClusterOverviewPrimarySection = (
  overview?: ClusterOverviewResponse
) => {
  if (overview?.agent?.summary || overview?.agent?.resourceTimeseries?.length) {
    return overview.agent;
  }

  return overview?.agentless;
};

export const mapClusterOverviewToSummaryNormalized = (
  overview?: ClusterOverviewResponse
): ReturnType<typeof enhanceClusterSummary> | undefined => {
  if (!overview) {
    return undefined;
  }

  const section = getClusterOverviewPrimarySection(overview);
  const summary = section?.summary;
  const lastPoint = section?.resourceTimeseries?.at(-1);
  const zero = toBig(0);

  const cpuProvisioned =
    toNumOrNull(summary?.totalCpuProvisioned) ??
    toNumOrNull(overview.cpuProvisioned) ??
    toNumOrNull(lastPoint?.cpuProvisioned) ??
    0;
  const cpuRequested = toNumOrZero(overview.cpuRequested);
  const cpuUsed =
    toNumOrNull(lastPoint?.cpuUsed) ??
    deriveUsedFromProvisionedAndUtilization(
      cpuProvisioned,
      toNumOrNull(lastPoint?.cpuUtilization) ??
        toNumOrNull(summary?.avgCpuUtilization) ??
        toNumOrNull(overview.avgCpuUtilization) ??
        0
    );

  const ramProvisioned =
    toNumOrNull(summary?.totalRamProvisioned) ??
    toNumOrNull(overview.ramProvisioned) ??
    toNumOrNull(lastPoint?.ramProvisionedGib) ??
    0;
  const ramRequested = toNumOrZero(overview.ramRequested);
  const ramUsed =
    toNumOrNull(lastPoint?.ramUsedGib) ??
    deriveUsedFromProvisionedAndUtilization(
      ramProvisioned,
      toNumOrNull(lastPoint?.ramUtilization) ??
        toNumOrNull(summary?.avgRamUtilization) ??
        toNumOrNull(overview.avgRamUtilization) ??
        0
    );

  const gpuProvisioned = toNumOrZero(summary?.totalGpuProvisioned);
  const cpuAllocatable = toBig(cpuProvisioned);
  const ramAllocatable = toBig(ramProvisioned);
  const gpuAllocatable = toBig(gpuProvisioned);
  const cpuRequestedBig = toBig(cpuRequested);
  const ramRequestedBig = toBig(ramRequested);
  const cpuUsedBig = toBig(cpuUsed);
  const ramUsedBig = toBig(ramUsed);

  const onDemandCostHourly = toNumOrNull(summary?.onDemandCostHourly);
  const spotCostHourly = toNumOrNull(summary?.spotCostHourly);
  const totalCostHourly =
    toNumOrNull(summary?.totalCostHourly) ?? toNumOrNull(overview.costHourly);
  // Prefer lifecycle split; if only a total is present, park it on on-demand so
  // getMonthlyCostFromSummary still resolves.
  const costHourlyOnDemand =
    onDemandCostHourly ?? (spotCostHourly != null ? 0 : totalCostHourly ?? 0);
  const costHourlySpot = spotCostHourly ?? 0;

  const normalizedSummary: CostReportClusterSummaryNormalized = {
    clusterId: overview.clusterId,
    clusterScore: '',
    nodesCountTotal: toBig(summary?.totalNodes),
    nodesCountManagedTotal: zero,
    nodeCountOnDemand: toBig(summary?.onDemandNodeCount),
    nodeCountSpot: toBig(summary?.spotNodeCount),
    nodeCountSpotFallbackCastai: zero,
    nodeCountOnDemandCastai: zero,
    nodeCountSpotCastai: zero,
    cpuAllocatableTotal: cpuAllocatable,
    cpuRequestedTotal: cpuRequestedBig,
    cpuProvisionedTotal: cpuAllocatable,
    cpuCostHourlyTotal: toBig(totalCostHourly ?? 0),
    cpuUsed: cpuUsedBig,
    ramAllocatableTotal: ramAllocatable,
    ramRequestedTotal: ramRequestedBig,
    ramProvisionedTotal: ramAllocatable,
    ramUsed: ramUsedBig,
    gpuProvisionedTotal: gpuAllocatable,
    gpuAllocatableTotal: gpuAllocatable,
    gpuUsedTotal: zero,
    gpuRequestedTotal: zero,
    costHourlyOnDemand: toBig(costHourlyOnDemand),
    costHourlySpot: toBig(costHourlySpot),
    costHourlySpotFallback: zero,
    cpuAllocatableOnDemand: cpuAllocatable,
    cpuAllocatableSpot: zero,
    cpuAllocatableSpotFallback: zero,
    cpuRequestedOnDemand: cpuRequestedBig,
    cpuRequestedSpot: zero,
    cpuRequestedSpotFallback: zero,
    cpuProvisionedOnDemand: cpuAllocatable,
    cpuProvisionedSpot: zero,
    cpuProvisionedSpotFallback: zero,
    ramAllocatableOnDemand: ramAllocatable,
    ramAllocatableSpot: zero,
    ramAllocatableSpotFallback: zero,
    ramRequestedOnDemand: ramRequestedBig,
    ramRequestedSpot: zero,
    ramRequestedSpotFallback: zero,
    ramProvisionedOnDemand: ramAllocatable,
    ramProvisionedSpot: zero,
    ramProvisionedSpotFallback: zero,
    gpuProvisionedOnDemand: gpuAllocatable,
    gpuProvisionedSpot: zero,
    gpuProvisionedSpotFallback: zero,
    gpuAllocatableOnDemand: gpuAllocatable,
    gpuAllocatableSpot: zero,
    gpuAllocatableSpotFallback: zero,
    gpuRequestedOnDemand: zero,
    gpuRequestedSpot: zero,
    gpuRequestedSpotFallback: zero,
    gpuUsedOnDemand: zero,
    gpuUsedSpot: zero,
    gpuUsedSpotFallback: zero,
    gpuIdleOnDemand: zero,
    gpuIdleSpot: zero,
    gpuIdleSpotFallback: zero,
    gpuNotUsedOnDemand: zero,
    gpuNotUsedSpot: zero,
    gpuNotUsedSpotFallback: zero,
    cpuCostHourlyOnDemand: zero,
    cpuCostHourlySpot: zero,
    cpuCostHourlySpotFallback: zero,
    ramCostHourlyOnDemand: zero,
    ramCostHourlySpot: zero,
    ramCostHourlySpotFallback: zero,
    gpuCostHourlyOnDemand: zero,
    gpuCostHourlySpot: zero,
    gpuCostHourlySpotFallback: zero,
    tpuAllocatableOnDemand: zero,
    tpuAllocatableSpot: zero,
    tpuAllocatableSpotFallback: zero,
    tpuCostHourlyOnDemand: zero,
    tpuCostHourlySpot: zero,
    tpuCostHourlySpotFallback: zero,
    tpuProvisionedOnDemand: zero,
    tpuProvisionedSpot: zero,
    tpuProvisionedSpotFallback: zero,
    tpuRequestedOnDemand: zero,
    tpuRequestedSpot: zero,
    tpuRequestedSpotFallback: zero,
    podCount: zero,
    unschedulablePodCount: zero,
    unknownNodeCount: zero,
    scheduledPodsCount: zero,
    storageProvisioned: toBig(lastPoint?.storageProvisionedGib),
    storageRequested: toBig(lastPoint?.storageRequestedGib),
    storageClaimed: toBig(lastPoint?.storageClaimedGib),
    storageCostHourly: zero,
  };

  return enhanceClusterSummary(normalizedSummary);
};

export const mapClusterOverviewTimeseriesToResourceChartDatapoints = (
  points: OrganizationOverviewResourceTimeseriesPoint[] = []
): ResourceChartDatapoints => {
  const ordered = orderBy(
    points.filter((point) => !!point.timestamp),
    'timestamp'
  );

  return ordered.reduce(
    (acc, point) => {
      const timestamp = dayjs.utc(point.timestamp).toISOString();

      const cpuProvisioned = toNumOrNull(point.cpuProvisioned);
      const cpuRequested = toNumOrNull(point.cpuRequested);
      const cpuUsed = toNumOrNull(point.cpuUsed);
      if (
        acc.cpu.hasNoData &&
        (cpuProvisioned != null || cpuRequested != null || cpuUsed != null)
      ) {
        acc.cpu.hasNoData = false;
      }
      acc.cpu.datapoints.push({
        timestamp,
        provisioned: cpuProvisioned,
        requested: cpuRequested,
        used: cpuUsed,
      });

      const ramProvisioned = toNumOrNull(point.ramProvisionedGib);
      const ramRequested = toNumOrNull(point.ramRequestedGib);
      const ramUsed = toNumOrNull(point.ramUsedGib);
      if (
        acc.memory.hasNoData &&
        (ramProvisioned != null || ramRequested != null || ramUsed != null)
      ) {
        acc.memory.hasNoData = false;
      }
      acc.memory.datapoints.push({
        timestamp,
        provisioned: ramProvisioned,
        requested: ramRequested,
        used: ramUsed,
      });

      acc.gpu.datapoints.push({
        timestamp,
        gpuMemoryUsed: null,
        gpuUsed: null,
        gpuRequested: null,
      });

      const storageProvisioned = toNumOrNull(point.storageProvisionedGib);
      const storageRequested = toNumOrNull(point.storageRequestedGib);
      if (
        acc.storage.hasNoData &&
        (storageProvisioned != null || storageRequested != null)
      ) {
        acc.storage.hasNoData = false;
      }
      acc.storage.datapoints.push({
        timestamp,
        provisioned: storageProvisioned,
        requested: storageRequested,
      });

      return acc;
    },
    {
      cpu: { datapoints: [], hasNoData: true },
      memory: { datapoints: [], hasNoData: true },
      gpu: { datapoints: [], hasNoData: true },
      storage: { datapoints: [], hasNoData: true },
    } as ResourceChartDatapoints
  );
};

export const mapClusterOverviewOomKillsTimeseries = (
  points: ClusterOverviewOomKillsPoint[] = []
): {
  chartItems: OomKillDataPoint[];
  totals: {
    managed: number | null;
    unmanaged: number | null;
    total: number;
  };
} => {
  // Overview exposes aggregate OOM counts only — do not invent a lifecycle split.
  const chartItems = orderBy(
    points.filter((point) => !!point.timestamp),
    'timestamp'
  ).map((point) => ({
    timestamp: dayjs.utc(point.timestamp).toISOString(),
    managedCount: null as number | null,
    unmanagedCount: null as number | null,
    totalCount: point.count ?? 0,
  }));

  const total = sumBy(chartItems, (item) => item.totalCount ?? 0);

  return {
    chartItems,
    totals: {
      managed: null,
      unmanaged: null,
      total,
    },
  };
};

export const buildServiceHealthMetricsFromClusterOverview = (
  slo?: OrganizationOverviewSloMetricValue[]
): SloColumnData => {
  const { metrics, breachCount, riskCount } = mapOverviewSloToColumnData(slo);
  const byId = new Map(metrics.map((metric) => [metric.id, metric]));

  const LABEL_BY_ID: Record<string, string> = {
    nodeAvailability: 'Node availability',
    latency: 'Latency',
    errRate: 'Error rates',
    oom: 'OOMKills',
  };

  // Keep tile slots stable when a metric is absent from the sparse payload.
  const orderedMetrics: SloMetric[] = SERVICE_HEALTH_METRIC_ORDER.map((id) => {
    const existing = byId.get(id);
    if (existing) {
      return existing;
    }

    return {
      id,
      label: LABEL_BY_ID[id] ?? id,
      value: EMPTY_METRIC_VALUE,
      status: 'ok',
    };
  });

  return {
    metrics: orderedMetrics,
    breachCount,
    riskCount,
  };
};
